import { UnitMode } from "~/domains/userPreferences/types/UnitMode";
import type { MouseEvent } from "react";
import { useTranslation } from "react-i18next";
import { ToggleButton, ToggleButtonGroup } from "~/common/mui/Button/ToggleButton";
import { InputLabel } from "~/common/mui";
import styled from "@emotion/styled";
import { useReduxUnitMode } from "~/domains/userPreferences/hooks/useReduxUnitMode";

export type GAPageType = "Navigation" | "Search" | "PDP" | "PLP" | "Comparison List" | "SA";

interface Props extends Omit<JSX.IntrinsicElements["div"], "children" | "onChange"> {
  value: UnitMode;
  onChange: (unit: UnitMode) => void;
  fullWidth?: boolean;
}

const ToggleContainer = styled("div", {
  shouldForwardProp: (prop) => prop !== "fullWidth",
})<{ fullWidth: boolean }>(({ fullWidth }) => ({
  maxWidth: fullWidth ? "100%" : "354px", //Max width added on request from SCHAEFF-34146
}));

export const UnitModeToggle = ({ onChange, value, fullWidth = false, ...props }: Props) => {
  const { t } = useTranslation();

  const handleChange = (_event: MouseEvent<HTMLElement>, nextValue: UnitMode | null) => {
    if (nextValue !== null) {
      onChange(nextValue);
    }
  };

  return (
    <ToggleContainer fullWidth={fullWidth} {...props}>
      <InputLabel shrink>{t("feature.toggle.unitSystem")}</InputLabel>
      <ToggleButtonGroup exclusive fullWidth value={value} showCheckmarks={true} onChange={handleChange}>
        <ToggleButton value={UnitMode.Metric}>{t("common.measureSystem.metric")}</ToggleButton>
        <ToggleButton value={UnitMode.Imperial}>{t("common.measureSystem.imperial")}</ToggleButton>
      </ToggleButtonGroup>
    </ToggleContainer>
  );
};

interface ReduxImperialToggleProps extends Omit<Props, "onChange" | "value"> {
  page: GAPageType;
  onChange?: (unitMode: UnitMode) => void;
}

export const ReduxImperialToggle = ({ page, onChange, ...props }: ReduxImperialToggleProps) => {
  const { value, onChange: handleOnChange } = useReduxUnitMode(page, onChange);
  return <UnitModeToggle value={value} onChange={handleOnChange} {...props} />;
};
